<div class="advertiser-detail-dialog">
  <div class="detail-header">
    <div class="detail-identity" *ngIf="user; else headerFallback">
      <span class="detail-avatar">{{ initials }}</span>
      <div class="detail-identity__text">
        <h2 class="detail-title">{{ displayName }}</h2>
        <p class="detail-subtitle">{{ user.email || "—" }}</p>
        <div class="detail-pills">
          <span
            class="status-badge"
            [class.badge-verified]="user.verification_status === 'verified'"
            [class.badge-pending]="user.verification_status !== 'verified'">
            {{ user.verification_status === "verified" ? "Verified" : "Pending" }}
          </span>
          <span
            class="status-badge"
            [class.badge-verified]="user.is_active"
            [class.badge-pending]="!user.is_active">
            {{ user.is_active ? "Active" : "Inactive" }}
          </span>
          <span
            class="status-badge"
            [class.badge-ordered]="user.order_status === 'ordered'"
            [class.badge-not-ordered]="user.order_status !== 'ordered'">
            {{ user.order_status === "ordered" ? "Ordered" : "Not ordered" }}
          </span>
        </div>
      </div>
    </div>
    <ng-template #headerFallback>
      <div class="detail-identity">
        <h2 class="detail-title">Advertiser details</h2>
      </div>
    </ng-template>
    <button type="button" class="detail-close-btn" (click)="close()" aria-label="Close advertiser details">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="detail-content">
    <div class="detail-state" *ngIf="loading">
      <app-data-loader [show]="true" message="Loading advertiser details..." size="small" />
    </div>

    <p class="detail-error" *ngIf="!loading && error">{{ error }}</p>

    <ng-container *ngIf="!loading && user">
      <div class="detail-stats">
        <div class="detail-stat">
          <span class="detail-stat__value">{{ user.order_count | number }}</span>
          <span class="detail-stat__label">Orders</span>
        </div>
        <div class="detail-stat">
          <span class="detail-stat__value">{{ formatMoney(user.total_order_value) }}</span>
          <span class="detail-stat__label">Order value</span>
        </div>
        <div class="detail-stat">
          <span class="detail-stat__value">{{ user.recordings_count | number }}</span>
          <span class="detail-stat__label">Recordings</span>
        </div>
        <div class="detail-stat">
          <span class="detail-stat__value">{{ user.commercial_requests_count | number }}</span>
          <span class="detail-stat__label">Requests</span>
        </div>
        <div class="detail-stat">
          <span class="detail-stat__value">{{ user.stations_ordered_count | number }}</span>
          <span class="detail-stat__label">Stations</span>
        </div>
      </div>

      <section class="detail-section">
        <h3>Profile</h3>
        <div class="detail-grid">
          <div>
            <strong>Name</strong>
            <span>{{ displayName }}</span>
          </div>
          <div class="detail-email">
            <strong>Email</strong>
            <span class="detail-email__value" [attr.title]="user.email || null">{{ user.email || "—" }}</span>
          </div>
          <div>
            <strong>Phone</strong>
            <span>{{ user.phonenumber || "—" }}</span>
          </div>
          <div>
            <strong>Account type</strong>
            <span>{{ accountTypeLabel(user.account_type) }}</span>
          </div>
          <div *ngIf="user.account_type === 'business'">
            <strong>Business name</strong>
            <span>{{ providedText(user.business_name) }}</span>
          </div>
          <div *ngIf="user.account_type === 'individual'">
            <strong>Signup purpose</strong>
            <span>{{ providedText(user.signup_purpose) }}</span>
          </div>
          <div>
            <strong>Account status</strong>
            <span
              class="status-badge"
              [class.badge-verified]="user.is_active"
              [class.badge-pending]="!user.is_active">
              {{ user.is_active ? "Active" : "Inactive" }}
            </span>
          </div>
        </div>
      </section>

      <section class="detail-section">
        <h3>Verification</h3>
        <div class="detail-grid">
          <div>
            <strong>Status</strong>
            <span
              class="status-badge"
              [class.badge-verified]="user.verification_status === 'verified'"
              [class.badge-pending]="user.verification_status !== 'verified'">
              {{ user.verification_status === "verified" ? "Verified" : "Pending" }}
            </span>
          </div>
          <div>
            <strong>Verified on</strong>
            <span>{{ user.email_verified_at ? formatDateTime(user.email_verified_at) : "—" }}</span>
          </div>
          <div>
            <strong>Days to verify</strong>
            <span>{{ user.days_to_verify != null ? user.days_to_verify : "—" }}</span>
          </div>
          <div>
            <strong>Signup date</strong>
            <span>{{ formatDate(user.signup_date) }}</span>
          </div>
        </div>
      </section>

      <section class="detail-section">
        <h3>Activity</h3>
        <div class="detail-grid">
          <div>
            <strong>Last login</strong>
            <span>{{ formatDateTime(user.last_login) }}</span>
          </div>
          <div>
            <strong>SMS notifications</strong>
            <span>{{ user.sms_notifications_enabled ? "Enabled" : "Disabled" }}</span>
          </div>
          <div>
            <strong>Inactivity reminders</strong>
            <span>{{ user.inactivity_reminder_count | number }}</span>
          </div>
          <div>
            <strong>Last inactivity reminder</strong>
            <span>{{ formatReminderSent(user.last_inactivity_reminder_sent_at) }}</span>
          </div>
        </div>
      </section>

      <section class="detail-section">
        <h3>Orders</h3>
        <div class="detail-grid">
          <div>
            <strong>Order status</strong>
            <span
              class="status-badge"
              [class.badge-ordered]="user.order_status === 'ordered'"
              [class.badge-not-ordered]="user.order_status !== 'ordered'">
              {{ user.order_status === "ordered" ? "Ordered" : "Not ordered" }}
            </span>
          </div>
          <div>
            <strong>First order</strong>
            <span>{{ user.first_order_at ? formatDate(user.first_order_at) : "—" }}</span>
          </div>
          <div>
            <strong>Days to first order</strong>
            <span>{{ user.days_to_first_order != null ? user.days_to_first_order : "—" }}</span>
          </div>
        </div>
      </section>

      <section class="detail-section">
        <div class="detail-section__title">
          <h3>Pages visited</h3>
          <span class="detail-source">{{ activitySourceLabel }}</span>
        </div>
        <p class="detail-empty" *ngIf="!hasPageActivity && activityFunnel?.available">
          No signed-in page visits in this window yet. They show up after this person uses the app.
        </p>
        <p class="detail-empty" *ngIf="!hasPageActivity && activityFunnel && !activityFunnel.available">
          No activity data available yet
        </p>
        <div class="page-list" *ngIf="hasPageActivity">
          <div class="page-list__row page-list__row--head">
            <span>Page</span>
            <span>Visits</span>
          </div>
          <div class="page-list__row" *ngFor="let page of activityFunnel!.pages">
            <span class="page-path">{{ pageLabel(page.page_path) }}</span>
            <span>{{ page.screen_page_views | number }}</span>
          </div>
        </div>
      </section>
    </ng-container>
  </div>
</div>
